// React Router future flags to suppress v7 warnings
// These must be set before React Router is imported
// Setting these flags should suppress the v7 warnings about startTransition and relativeSplatPath
window.__REACT_ROUTER_FUTURE__ = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
  v7_normalizeFormMethod: true,
  v7_prependBasename: true,
};

// Also try setting them as individual properties to ensure they are set
window.__REACT_ROUTER_FUTURE__ = window.__REACT_ROUTER_FUTURE__ || {};
window.__REACT_ROUTER_FUTURE__.v7_startTransition = true;
window.__REACT_ROUTER_FUTURE__.v7_relativeSplatPath = true;
window.__REACT_ROUTER_FUTURE__.v7_normalizeFormMethod = true;
window.__REACT_ROUTER_FUTURE__.v7_prependBasename = true;

import './i18n';

import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';

import App from './App';
import { store } from './store';
import { COLORS, DARK_BG, LIGHT_BG, PRIMARY, SECONDARY } from './theme/palette';

// Create a client for React Query with optimized defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      // Enable suspense mode for better loading states
      suspense: false,
      // Cache data for 5 minutes by default
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 10 minutes
      cacheTime: 10 * 60 * 1000,
      // Use cached data while refetching
      keepPreviousData: true,
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

const getDesignTokens = (mode, language) => ({
  palette: {
    mode,
    primary: PRIMARY,
    secondary: SECONDARY,
    background:
      mode === 'light'
        ? { default: LIGHT_BG.primary, paper: LIGHT_BG.paper }
        : { default: DARK_BG.primary, paper: DARK_BG.secondary },
  },
  typography: {
    fontFamily: (language || 'en').startsWith('ja')
      ? '"Noto Sans JP", "Hiragino Kaku Gothic ProN", "Yu Gothic", "Meiryo", "MS PGothic", Roboto, Arial, sans-serif'
      : 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', borderRadius: 8 },
      },
    },
    // Ensure dropdown menus are opaque in light mode (prevents see-through menus).
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow:
            mode === 'light'
              ? '0 2px 8px rgba(0,0,0,0.1)'
              : '0 2px 8px rgba(0,0,0,0.5)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Fonts are loaded from Google Fonts via index.html
        // No need for local @font-face declarations
      },
    },
  },
});

const ThemedApp = () => {
  const mode = useSelector(state => state.ui.theme || 'light');
  const { i18n } = useTranslation();
  const language = i18n.language || 'en';
  const theme = React.useMemo(
    () => createTheme(getDesignTokens(mode, language)),
    [mode, language]
  );
  const [splash, setSplash] = React.useState(true);

  React.useEffect(() => {
    // Hide splash immediately when component mounts - don't wait for full load
    const timer = setTimeout(() => setSplash(false), 100);
    return () => clearTimeout(timer);
  }, []);

  React.useEffect(() => {
    // Remove pre-splash element and restore scrolling when React splash handled
    if (!splash) {
      const el = document.getElementById('pre-splash');
      if (el) {
        // Immediately hide splash - no delay for better LCP
        el.classList.add('ps-hide');
        // Clean up DOM after transition completes
        setTimeout(() => {
          if (el && el.parentNode) el.parentNode.removeChild(el);
        }, 300); // Match CSS transition time
      }
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  }, [splash]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={theme => ({
          'html, body': {
            height: '100%',
            margin: 0,
            padding: 0,
            overscrollBehaviorY: 'none',
            overflowX: 'hidden',
            backgroundColor:
              theme.palette.mode === 'light'
                ? '#f5f5f5'
                : theme.palette.background.default,
          },
          '*, *::before, *::after': {
            scrollbarWidth: 'thin',
            scrollbarColor:
              theme.palette.mode === 'light'
                ? '#b8b8b8 #f1f1f1'
                : '#4b5563 #0f1115',
          },
          '*::-webkit-scrollbar': {
            width: 10,
            height: 10,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor:
              theme.palette.mode === 'light'
                ? '#f1f1f1'
                : theme.palette.background.default,
          },
          '*::-webkit-scrollbar-thumb': {
            background:
              theme.palette.mode === 'light'
                ? 'linear-gradient(180deg,#c9c9c9,#a9a9a9)'
                : 'linear-gradient(180deg,#3b3f46,#566173)',
            borderRadius: 8,
            border: `2px solid ${theme.palette.background.default}`,
          },
          '*::-webkit-scrollbar-thumb:hover': {
            background: theme.palette.mode === 'light' ? '#9a9a9a' : '#647085',
          },
        })}
      />
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
          v7_normalizeFormMethod: true,
          v7_prependBasename: true,
        }}
      >
        <App />
        <Toaster
          position='top-right'
          toastOptions={{
            duration: 4000,
            style: {
              background: mode === 'light' ? '#363636' : '#1f2937',
              color: COLORS.white,
            },
          }}
        />
      </BrowserRouter>
    </ThemeProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
      </QueryClientProvider>
    </Provider>
  </React.StrictMode>
);
